/**
 * RxCheckUp — Unified Brand Logo Component
 *
 * Usage:
 *   <Logo />                       → Default (md) inline logo with wordmark
 *   <Logo size="sm" />             → Small nav logo
 *   <Logo size="lg" />             → Large hero/login logo
 *   <Logo iconOnly />              → Icon mark only (no text)
 *   <Logo subtitle="LMN Generator" /> → With subtitle
 *   <Logo variant="admin" />       → Admin badge
 */

interface LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  iconOnly?: boolean
  subtitle?: string
  variant?: 'default' | 'admin'
  className?: string
}

const sizes = {
  sm: { box: 'w-7 h-7', rx: 'text-[9px]', check: 'w-[10px] h-[10px]', text: 'text-base', sub: 'text-[9px]', radius: 'rounded-lg', gap: 'gap-2' },
  md: { box: 'w-8 h-8', rx: 'text-[10px]', check: 'w-[11px] h-[11px]', text: 'text-lg', sub: 'text-[10px]', radius: 'rounded-lg', gap: 'gap-2.5' },
  lg: { box: 'w-10 h-10', rx: 'text-xs', check: 'w-[13px] h-[13px]', text: 'text-2xl', sub: 'text-xs', radius: 'rounded-xl', gap: 'gap-3' },
  xl: { box: 'w-14 h-14', rx: 'text-sm', check: 'w-4 h-4', text: 'text-3xl', sub: 'text-sm', radius: 'rounded-2xl', gap: 'gap-3.5' },
}

export default function Logo({ size = 'md', iconOnly = false, subtitle, variant = 'default', className = '' }: LogoProps) {
  const s = sizes[size]

  return (
    <div className={`flex items-center ${s.gap} ${className}`}>
      {/* Icon Mark */}
      <div className={`${s.box} ${s.radius} bg-gradient-to-br from-teal-600 to-teal-700 flex items-center justify-center relative overflow-hidden shadow-sm`}>
        {/* Rx text */}
        <span className={`${s.rx} font-bold text-white leading-none`} style={{ fontFamily: "'Inter', sans-serif", marginRight: '2px' }}>
          Rx
        </span>
        {/* Checkmark */}
        <svg className={`${s.check} text-white absolute`}
          style={{ bottom: size === 'sm' ? '3px' : size === 'md' ? '3px' : size === 'lg' ? '4px' : '5px', right: size === 'sm' ? '2px' : size === 'md' ? '2px' : size === 'lg' ? '3px' : '4px' }}
          viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 8.5l3.5 3.5L13 5" />
        </svg>
      </div>

      {/* Wordmark */}
      {!iconOnly && (
        <div className="leading-none">
          <span className={`${s.text} font-bold text-slate-800 tracking-tight`}>
            Rx<span className="text-teal-600">CheckUp</span><sup className="text-[9px] font-medium text-slate-400 ml-px" style={{ verticalAlign: 'super', fontSize: size === 'sm' ? '7px' : size === 'md' ? '8px' : size === 'lg' ? '9px' : '10px' }}>™</sup>
          </span>
          {subtitle && (
            <span className={`${s.sub} text-slate-400 font-semibold ml-2 uppercase tracking-widest`}>{subtitle}</span>
          )}
          {variant === 'admin' && (
            <span className={`${s.sub} text-teal-600 font-semibold ml-2 bg-teal-50 px-2 py-0.5 rounded-full`}>ADMIN</span>
          )}
        </div>
      )}
    </div>
  )
}
